import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageState } from '@flabs/models/page';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from '@flabs/core/services/auth.service';
import { first, takeUntil } from 'rxjs/operators';
import { AlertService } from '@flabs/core/services/alert.service';
import { Subject } from 'rxjs';
import { TokenService } from '@flabs/core/services/token.service';
import { Login } from '@flabs/models/auth';
import { ColorService } from '@flabs/core/services/color.service';
import { StaticPagesPopupComponent } from '../static-pages/static-pages-popup/static-pages-popup.component';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'flabs-login',
  templateUrl: './login.component.html',
  styleUrls: ['../single-card-page.scss', './login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: UntypedFormGroup = this.fb.group({
    password: ['', [Validators.required]],
    username: ['', [Validators.required]],
  });

  submitted = false;
  pageState: PageState = new PageState();
  isLoggedOut = false;
  isSessionExpired = false;
  token: string;
  currentYear: number;
  tokenError: string;
  private returnUrl: string;
  private unsubscribe$ = new Subject<any>();
  logoUrl = this.colorService.DefaultColors.logoUrl;
  modal: NgbModalRef;
  private ngbModalOptions: NgbModalOptions = {
    windowClass: 'flabs-modal-large',
    backdrop: 'static',
  };
  userStatus: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private alertService: AlertService,
    private tokenService: TokenService,
    private changeDetector: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private colorService: ColorService,
    private modalService: NgbModal,
  ) {
  }

  ngOnInit() {
    if (!!this.authService.currentUserValue) {
      this.router.navigate(['/app/workspaces']);
    }
    this.currentYear = new Date().getFullYear();
    this.authService.serverStatus().subscribe(
      () => {
      },
      (err) => {
        if (err && err.status === 503) {
          this.router.navigate(['/maintenance'], { queryParams: { returnUrl: this.router.routerState.snapshot.url } });
        }
      },
    );
    if (this.authService.currentColorValue) {
      this.logoUrl = this.authService.currentColorValue.logoUrl ? this.authService.currentColorValue.logoUrl : './assets/images/logo.png';
    }
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/app/workspaces';
    this.isLoggedOut = this.route.snapshot.queryParams['logout'] !== undefined;
    this.isSessionExpired = this.route.snapshot.queryParams['expired'] !== undefined;
    this.isHaveToken();
    if (this.route.snapshot.queryParams.email) {
      this.loginForm.patchValue({ username: decodeURIComponent(this.route.snapshot.queryParams.email) });
    }
  }

  emailInput() {
    if (!!this.control('username').value) {
      this.control('username').patchValue(this.control('username').value.trim());
    }
  }

  async isHaveToken() {
    const queryToken = this.route.snapshot.queryParams.token;
    if (queryToken) {
      await this.tokenService.checkToken(queryToken).subscribe(
        (resp) => {
          if (resp.available) {
            this.token = queryToken;
            this.loginForm.patchValue({ username: decodeURIComponent(this.route.snapshot.queryParams.email) });
          } else {
            this.tokenError = 'TOKEN_' + resp.reason;
          }
          this.changeDetector.markForCheck();
        },
        (_) => {
          this.tokenError = 'ERROR';
          this.changeDetector.markForCheck();
        },
      );
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.unsubscribe();
  }

  get f() {
    return this.loginForm.controls;
  }

  login(): void {
    this.submitted = true;
    if (this.loginForm.invalid) {
      return;
    }
    let userData: Login;
    if (this.token) {
      userData = {
        ...this.loginForm.value,
        token: this.token,
      };
    } else {
      userData = {
        ...this.loginForm.value,
      };
    }
    const methodName = this.token ? 'loginWithToken' : 'login';
    this.pageState.loading = true;
    this.authService[methodName](userData)
      .pipe(first(), takeUntil(this.unsubscribe$))
      .subscribe(
        (data) => {
          this.router.navigate([this.returnUrl]);
        },
        (error) => {
          this.loginForm.setErrors({ invalid: true });
          this.loginForm.patchValue({ password: '' });
          if (error.status === 401 || error.status === 400) {
            this.loginForm.setErrors({ invalid: true });
            switch (error.error && error.error.code) {
              case 'LOCKED_ACCOUNT':
                this.userStatus = 'locked';
                break;
              case 'DISABLED_ACCOUNT':
                this.userStatus = 'blocked';
                break;
              default:
                this.userStatus = 'invalid';
            }
          } else {
            this.alertService.error(error.error.code);
          }
          this.pageState.loading = false;
          this.changeDetector.detectChanges();
        },
      );
  }

  control(name: string): AbstractControl {
    return this.loginForm.controls[name];
  }

  array(name: string): UntypedFormArray {
    return this.loginForm.controls[name] as UntypedFormArray;
  }

  showErrorFor(name: string): boolean {
    return this.control(name).invalid && this.control(name).touched;
  }

  public openStaticPagesModal(page) {
    this.modal = this.modalService.open(StaticPagesPopupComponent, this.ngbModalOptions);
    this.modal.componentInstance.page = page;
  }
}
